import React, { useLayoutEffect, useState } from "react";
import styles from "../Organigrama.module.scss";

/**
 * PROTOTIPO — Capa SVG que dibuja los conectores padre→hijo de la cascada.
 *
 * Mide la posición real de cada card (por `data-nid`) dentro de la grilla y traza un
 * conector ORTOGONAL (elbow) del borde derecho del padre al borde izquierdo del hijo:
 * como todos los hijos de un padre comparten el mismo `mx`, los tramos verticales se
 * superponen y se ve como un "tronco" con ramas (estilo organigrama).
 *
 * Se recalcula al cambiar el layout (expandir/colapsar → cambia `placements`) y al
 * redimensionar (ResizeObserver sobre la grilla). No bloquea clicks (pointer-events:none).
 * Vive DENTRO de la grilla, así que scrollea junto con las cards (siempre alineado).
 */
export default function ConectoresCascada({ gridRef, placements, padrePorId }) {
    const [edges, setEdges] = useState([]);

    useLayoutEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        const medir = () => {
            // Posición de cada card (relativa a la grilla, que es position:relative).
            const pos = new Map();
            grid.querySelectorAll("[data-nid]").forEach((el) => {
                pos.set(el.getAttribute("data-nid"), {
                    left: el.offsetLeft,
                    top: el.offsetTop,
                    width: el.offsetWidth,
                    height: el.offsetHeight,
                });
            });

            // Agrupar hijos visibles por padre: UN tronco por padre + UNA rama a cada hijo
            // (a su centro). Así ninguna card recibe más de una línea.
            const grupos = new Map(); // pid → { p, hijos: rect[] }
            placements.forEach(({ nodo }) => {
                const pid = padrePorId.get(nodo.rnhEstablecimientoEstructuraId);
                if (pid == null) return; // raíz (Dirección): sin padre
                const c = pos.get(String(nodo.rnhEstablecimientoEstructuraId));
                const p = pos.get(String(pid));
                if (!c || !p) return;
                if (!grupos.has(pid)) grupos.set(pid, { p, hijos: [] });
                grupos.get(pid).hijos.push(c);
            });

            const es = [];
            grupos.forEach(({ p, hijos }, pid) => {
                const pRight = p.left + p.width;
                // Tronco CENTRADO en el gap entre el padre y su hijo más cercano.
                const hijoMasCercano = Math.min(...hijos.map((c) => c.left));
                const troncoX = pRight + (hijoMasCercano - pRight) / 2;
                // El primer hijo (el más arriba) comparte fila con el padre: el padre sale a
                // ESA altura, así su tramo y la rama del primer hijo quedan colineales (1 línea).
                const primeraYc = Math.min(...hijos.map((c) => c.top + c.height / 2));
                hijos.forEach((c, i) => {
                    const ex = c.left; // borde izquierdo del hijo
                    const ey = c.top + c.height / 2; // centro vertical del hijo
                    es.push({
                        key: `${pid}-${i}`,
                        d: `M ${pRight} ${primeraYc} H ${troncoX} V ${ey} H ${ex}`,
                    });
                });
            });
            setEdges(es);
        };

        medir();
        const ro = new ResizeObserver(medir);
        ro.observe(grid);
        return () => ro.disconnect();
    }, [gridRef, placements, padrePorId]);

    return (
        <svg className={styles.conectores} aria-hidden="true">
            {edges.map((e) => (
                <path key={e.key} d={e.d} />
            ))}
        </svg>
    );
}
